// src/pages/dashboard/branch/analytics/components/SalesChart.tsx
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    TooltipProps,
} from "recharts";
import { FiTrendingUp, FiBarChart2 } from "react-icons/fi";
import Card from "../../../../../components/ui/Card";
import Skeleton from "../../../../../components/ui/Skeleton";

type ChartType = "area" | "bar";

interface ChartDataPoint {
    date: string;
    sales: number;
    orders: number;
    [key: string]: string | number;
}

interface SalesChartProps {
    title: string;
    subtitle?: string;
    data: ChartDataPoint[];
    chartType?: ChartType;
    isLoading?: boolean;
    showOrders?: boolean;
    height?: number;
    currencyCode?: string;
    currencySymbol?: string;
    formatCurrency?: (value: number) => string;
}

const CustomTooltip: React.FC<TooltipProps<number, string> & { formatCurrency?: (value: number) => string }> = ({
    active,
    payload,
    label,
    formatCurrency,
}) => {
    if (!active || !payload?.length) return null;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700"
        >
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                {label}
            </p>
            <div className="space-y-1">
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            {entry.name}:
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {entry.name?.toLowerCase().includes("sales") || entry.name?.toLowerCase().includes("revenue")
                                ? formatCurrency
                                    ? formatCurrency(entry.value as number)
                                    : `$${(entry.value as number).toFixed(2)}`
                                : entry.value}
                        </span>
                    </div>
                ))}
            </div>
        </motion.div>
    );
};

const ChartSkeleton: React.FC<{ height: number }> = ({ height }) => (
    <Card>
        <div className="flex items-center gap-2 mb-4">
            <Skeleton height="h-5" width="w-5" rounded="rounded" />
            <Skeleton height="h-6" width="w-40" />
        </div>
        <div style={{ height }} className="relative">
            <div className="absolute inset-0 flex items-end gap-2 p-4">
                {Array.from({ length: 12 }).map((_, i) => (
                    <div
                        key={i}
                        className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-t animate-pulse"
                        style={{ height: `${30 + Math.random() * 60}%` }}
                    />
                ))}
            </div>
        </div>
    </Card>
);

const SalesChart: React.FC<SalesChartProps> = memo(({
    title,
    subtitle,
    data,
    chartType = "bar",
    isLoading = false,
    showOrders = true,
    height = 320,
    currencyCode,
    currencySymbol,
    formatCurrency,
}) => {
    const { t } = useTranslation();

    if (isLoading) {
        return <ChartSkeleton height={height} />;
    }

    if (!data || data.length === 0) {
        return (
            <Card>
                <div className="flex items-center gap-2 mb-4">
                    <FiBarChart2 className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {title}
                    </h3>
                </div>
                <div className="flex flex-col items-center justify-center py-16">
                    <FiTrendingUp className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                        {t("analytics.noDataForPeriod")}
                    </p>
                </div>
            </Card>
        );
    }

    const Icon = chartType === "area" ? FiTrendingUp : FiBarChart2;
    const currencyLabel = currencyCode || currencySymbol || "USD";
    const symbol = currencySymbol || "";
    const formatMonetary = (value: number) => {
        if (formatCurrency) {
            return formatCurrency(value);
        }
        return symbol ? `${symbol}${value.toFixed(2)}` : `$${value.toFixed(2)}`;
    };
    const formatAxisTick = (value: number) => {
        if (formatCurrency) {
            return formatCurrency(value).replace(/[^\d.,-]/g, "");
        }
        if (Math.abs(value) >= 1000) {
            const shortValue = value / 1000;
            return symbol ? `${symbol}${shortValue.toFixed(1)}k` : `$${shortValue.toFixed(1)}k`;
        }
        return symbol ? `${symbol}${value.toFixed(0)}` : `$${value.toFixed(0)}`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                            <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {title}
                            </h3>
                            {subtitle && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {subtitle}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ height }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {chartType === "area" ? (
                            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="ordersGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={false}
                                    stroke="currentColor"
                                    className="opacity-10"
                                />
                                <XAxis
                                    dataKey="date"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                    dx={-10}
                                    tickFormatter={formatAxisTick}
                                />
                                <Tooltip content={<CustomTooltip formatCurrency={formatMonetary} />} />
                                <Legend
                                    wrapperStyle={{ paddingTop: 20 }}
                                    iconType="circle"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="sales"
                                    name={`${t("analytics.sales")} (${currencyLabel})`}
                                    stroke="#10B981"
                                    strokeWidth={2.5}
                                    fill="url(#salesGradient)"
                                    dot={false}
                                    activeDot={{ r: 6, strokeWidth: 2, fill: "#fff", stroke: "#10B981" }}
                                />
                                {showOrders && (
                                    <Area
                                        type="monotone"
                                        dataKey="orders"
                                        name={t("analytics.orders")}
                                        stroke="#3B82F6"
                                        strokeWidth={2.5}
                                        fill="url(#ordersGradient)"
                                        dot={false}
                                        activeDot={{ r: 6, strokeWidth: 2, fill: "#fff", stroke: "#3B82F6" }}
                                    />
                                )}
                            </AreaChart>
                        ) : (
                            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="barSalesGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#10B981" />
                                        <stop offset="100%" stopColor="#059669" />
                                    </linearGradient>
                                    <linearGradient id="barOrdersGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#3B82F6" />
                                        <stop offset="100%" stopColor="#2563EB" />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={false}
                                    stroke="currentColor"
                                    className="opacity-10"
                                />
                                <XAxis
                                    dataKey="date"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                    dx={-10}
                                    tickFormatter={formatAxisTick}
                                />
                                <Tooltip content={<CustomTooltip formatCurrency={formatMonetary} />} />
                                <Legend
                                    wrapperStyle={{ paddingTop: 20 }}
                                    iconType="circle"
                                />
                                <Bar
                                    dataKey="sales"
                                    name={`${t("analytics.sales")} (${currencyLabel})`}
                                    fill="url(#barSalesGradient)"
                                    radius={[6, 6, 0, 0]}
                                    maxBarSize={40}
                                />
                                {showOrders && (
                                    <Bar
                                        dataKey="orders"
                                        name={t("analytics.orders")}
                                        fill="url(#barOrdersGradient)"
                                        radius={[6, 6, 0, 0]}
                                        maxBarSize={40}
                                    />
                                )}
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </Card>
        </motion.div>
    );
});

SalesChart.displayName = "SalesChart";

export default SalesChart;
